import { supabase } from '../lib/supabase'
import {
  buildPaymentRowsFromTemplates,
  paymentTemplatesService,
  snapshotPaymentRows,
  snapshotTemplateRows,
} from './paymentTemplates'
import type {
  MarketingExpense,
  MarketingExpenseAttachment,
  MarketingExpenseInput,
  SoftDeleteParams,
} from '../types/marketingDespesas'
import type { MarketingPayment, MarketingPaymentGroupInput } from '../types/marketingNotas'

const BUCKET = 'expenses'
const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024
const ALLOWED_ATTACHMENT_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
])

const EXPENSE_SELECT = `
  *,
  supplier:suppliers(id, name),
  payment_source:payment_sources(id, name),
  created_by_user:profiles!created_by(id, name),
  attachments:expense_attachments(*),
  payments(*),
  payment_templates(*)
`

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

function emptyToNull(value?: string | null): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function roundMoney(value: number): number {
  return Math.round(value * 10000) / 10000
}

function isAllowedAttachment(file: File): boolean {
  if (file.type && ALLOWED_ATTACHMENT_MIME.has(file.type)) return true
  const lower = file.name.toLowerCase()
  return (
    lower.endsWith('.pdf') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.png') ||
    lower.endsWith('.gif') ||
    lower.endsWith('.webp')
  )
}

function paymentGroupsTotal(payments: MarketingPaymentGroupInput[]): number {
  return roundMoney(
    payments.reduce(
      (acc, group) =>
        acc + group.installments.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      0
    )
  )
}

function validatePayments(amount: number, payments: MarketingPaymentGroupInput[]): void {
  if (!payments.length) {
    throw new Error('Informe ao menos uma forma de pagamento.')
  }
  for (const group of payments) {
    if (!group.payment_method_id?.trim()) {
      throw new Error('Selecione a forma de pagamento em todos os grupos.')
    }
    if (!group.installments.length) {
      throw new Error('Informe ao menos uma parcela.')
    }
    for (const installment of group.installments) {
      if (!installment.due_date?.trim()) {
        throw new Error('Informe o vencimento de todas as parcelas.')
      }
      const installmentAmount = Number(installment.amount)
      if (!Number.isFinite(installmentAmount) || installmentAmount <= 0) {
        throw new Error('Informe o valor de todas as parcelas.')
      }
    }
  }
  const allocated = paymentGroupsTotal(payments)
  if (Math.abs(allocated - amount) > 0.01) {
    throw new Error('O total das parcelas deve ser igual ao valor da despesa.')
  }
}

function validateInput(input: MarketingExpenseInput): {
  actionName: string
  supplierId: string
  paymentSourceId: string
  description: string | null
  amount: number
} {
  const actionName = input.action_name?.trim()
  if (!actionName) throw new Error('Informe o nome da ação/evento.')
  const supplierId = input.supplier_id?.trim()
  if (!supplierId) throw new Error('Selecione o fornecedor.')
  const paymentSourceId = input.payment_source_id?.trim()
  if (!paymentSourceId) throw new Error('Selecione a fonte pagadora.')
  const amount = Number(input.amount)
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Informe um valor maior que zero.')
  }
  validatePayments(roundMoney(amount), input.payments)
  return {
    actionName,
    supplierId,
    paymentSourceId,
    description: emptyToNull(input.description),
    amount: roundMoney(amount),
  }
}

function mapExpense(row: MarketingExpense): MarketingExpense {
  const attachments = ((row.attachments || []) as MarketingExpenseAttachment[])
    .filter((att) => !att.deleted_at)
    .sort((a, b) => a.created_at.localeCompare(b.created_at))

  const payments = ((row.payments || []) as MarketingPayment[])
    .filter((payment) => !payment.deleted_at)
    .map((payment) => ({
      ...payment,
      amount: payment.amount == null ? null : Number(payment.amount),
      group_index: Number(payment.group_index),
      installment_number: Number(payment.installment_number),
      expense_id: payment.expense_id ?? row.id,
    }))
    .sort((a, b) => {
      if (a.group_index !== b.group_index) return a.group_index - b.group_index
      return a.installment_number - b.installment_number
    })

  const paymentTemplates = (row.payment_templates || []).filter((template) => !template.deleted_at)

  return {
    ...row,
    amount: Number(row.amount),
    status: 'confirmed',
    description: row.description || null,
    supplier: row.supplier || null,
    payment_source: row.payment_source || null,
    attachments,
    payments,
    payment_templates: paymentTemplates,
    created_by_user: row.created_by_user || null,
  }
}

async function restoreExpenseFinancialState(
  expenseId: string,
  templates: ReturnType<typeof snapshotTemplateRows>,
  payments: ReturnType<typeof snapshotPaymentRows>
): Promise<void> {
  await paymentTemplatesService.restoreForOwner(
    { expense_id: expenseId },
    templates,
    { deleteReason: 'rollback restore templates' }
  )

  const rollbackPayload = softDeletePayload({ deleted_reason: 'rollback restore payments' })
  const { error: softPaymentsError } = await supabase
    .from('payments')
    .update(rollbackPayload)
    .eq('expense_id', expenseId)
    .is('deleted_at', null)
  if (softPaymentsError) throw softPaymentsError

  if (!payments.length) return
  const { error: insertPaymentsError } = await supabase.from('payments').insert(payments)
  if (insertPaymentsError) throw insertPaymentsError
}

export const marketingDespesasService = {
  async list(): Promise<MarketingExpense[]> {
    const { data, error } = await supabase
      .from('expenses')
      .select(EXPENSE_SELECT)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error
    return ((data || []) as MarketingExpense[]).map(mapExpense)
  },

  async get(id: string): Promise<MarketingExpense | null> {
    const { data, error } = await supabase
      .from('expenses')
      .select(EXPENSE_SELECT)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null
    return mapExpense(data as MarketingExpense)
  },

  async create(input: MarketingExpenseInput, createdBy?: string | null): Promise<MarketingExpense> {
    const validated = validateInput(input)

    const { data: header, error: headerError } = await supabase
      .from('expenses')
      .insert({
        action_name: validated.actionName,
        supplier_id: validated.supplierId,
        payment_source_id: validated.paymentSourceId,
        description: validated.description,
        amount: validated.amount,
        status: 'confirmed',
        created_by: createdBy ?? null,
      })
      .select('id')
      .single()

    if (headerError) throw headerError

    try {
      const templateResult = await paymentTemplatesService.replaceForOwner(
        { expense_id: header.id },
        input.payments,
        {
          actorId: createdBy ?? null,
          deleteReason: 'initial payment template',
        }
      )
      const paymentRows = buildPaymentRowsFromTemplates(templateResult.rows)
      const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
      if (paymentsError) throw paymentsError
    } catch (paymentFlowError) {
      const rollbackPayload = softDeletePayload({
        deleted_by: createdBy ?? null,
        deleted_reason: 'rollback create templates/payments',
      })
      await supabase
        .from('payment_templates')
        .update(rollbackPayload)
        .eq('expense_id', header.id)
        .is('deleted_at', null)
      await supabase
        .from('payments')
        .update(rollbackPayload)
        .eq('expense_id', header.id)
        .is('deleted_at', null)
      await supabase.from('expenses').update(rollbackPayload).eq('id', header.id)
      throw paymentFlowError
    }

    const created = await this.get(header.id)
    if (!created) throw new Error('Despesa criada, mas não foi possível recarregar.')
    return created
  },

  async update(id: string, input: MarketingExpenseInput): Promise<MarketingExpense> {
    const existing = await this.get(id)
    if (!existing) throw new Error('Despesa não encontrada.')

    const hasPaid = (existing.payments || []).some((payment) => payment.payment_status === 'paid')
    if (hasPaid) {
      throw new Error('Não é possível editar despesa com parcela já baixada.')
    }

    const validated = validateInput(input)
    const previousTemplates = snapshotTemplateRows(existing.payment_templates)
    const previousPayments = snapshotPaymentRows(existing.payments)

    const { error: headerError } = await supabase
      .from('expenses')
      .update({
        action_name: validated.actionName,
        supplier_id: validated.supplierId,
        payment_source_id: validated.paymentSourceId,
        description: validated.description,
        amount: validated.amount,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (headerError) throw headerError

    try {
      const replacePayload = softDeletePayload({ deleted_reason: 'replaced on update' })
      const { error: softPaymentsError } = await supabase
        .from('payments')
        .update(replacePayload)
        .eq('expense_id', id)
        .is('deleted_at', null)
      if (softPaymentsError) throw softPaymentsError

      const templateResult = await paymentTemplatesService.replaceForOwner(
        { expense_id: id },
        input.payments,
        { deleteReason: 'replaced on update' }
      )
      const paymentRows = buildPaymentRowsFromTemplates(templateResult.rows)
      const { error: paymentsError } = await supabase.from('payments').insert(paymentRows)
      if (paymentsError) throw paymentsError
    } catch (paymentFlowError) {
      await restoreExpenseFinancialState(id, previousTemplates, previousPayments)
      throw paymentFlowError
    }

    const updated = await this.get(id)
    if (!updated) throw new Error('Despesa atualizada, mas não foi possível recarregar.')
    return updated
  },

  async softDelete(id: string, params?: SoftDeleteParams): Promise<void> {
    const existing = await this.get(id)
    if (!existing) throw new Error('Despesa não encontrada.')

    const hasPaid = (existing.payments || []).some((payment) => payment.payment_status === 'paid')
    if (hasPaid) {
      throw new Error('Não é possível excluir despesa com parcela já baixada.')
    }

    const payload = softDeletePayload(params)
    const { error } = await supabase
      .from('expenses')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
    if (error) throw error

    const { error: templatesError } = await supabase
      .from('payment_templates')
      .update(payload)
      .eq('expense_id', id)
      .is('deleted_at', null)
    if (templatesError) throw templatesError

    const { error: paymentsError } = await supabase
      .from('payments')
      .update(payload)
      .eq('expense_id', id)
      .is('deleted_at', null)
    if (paymentsError) throw paymentsError

    const { error: attachmentsError } = await supabase
      .from('expense_attachments')
      .update(payload)
      .eq('expense_id', id)
      .is('deleted_at', null)
    if (attachmentsError) throw attachmentsError
  },

  async listAttachments(expenseId: string): Promise<MarketingExpenseAttachment[]> {
    const { data, error } = await supabase
      .from('expense_attachments')
      .select('*')
      .eq('expense_id', expenseId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })

    if (error) throw error
    return (data || []) as MarketingExpenseAttachment[]
  },

  async uploadAttachment(
    file: File,
    expenseId: string,
    uploadedBy?: string | null
  ): Promise<MarketingExpenseAttachment> {
    const existing = await this.get(expenseId)
    if (!existing) throw new Error('Despesa não encontrada.')

    if (!isAllowedAttachment(file)) {
      throw new Error('Arquivo não permitido. Use imagem ou PDF.')
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      throw new Error('Arquivo excede o limite de 20 MB.')
    }

    const timestamp = Date.now()
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const filePath = `${expenseId}/${timestamp}_${sanitizedFileName}`

    const { error: uploadError } = await supabase.storage.from(BUCKET).upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type,
    })

    if (uploadError) {
      throw new Error(`Falha ao enviar anexo: ${uploadError.message}`)
    }

    const { data, error } = await supabase
      .from('expense_attachments')
      .insert({
        expense_id: expenseId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        uploaded_by: uploadedBy || null,
      })
      .select('*')
      .single()

    if (error) {
      await supabase.storage.from(BUCKET).remove([filePath])
      throw error
    }

    return data as MarketingExpenseAttachment
  },

  async getAttachmentUrl(filePath: string, expiresInSeconds = 3600): Promise<string | null> {
    if (!filePath) return null
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, expiresInSeconds)
    if (error) {
      console.error('Erro ao gerar URL assinada do recibo:', error.message)
      return null
    }
    return data?.signedUrl ?? null
  },

  async softDeleteAttachment(id: string, params?: SoftDeleteParams): Promise<void> {
    const { data: att, error: fetchError } = await supabase
      .from('expense_attachments')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!att) return

    const { error } = await supabase
      .from('expense_attachments')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    try {
      await supabase.storage.from(BUCKET).remove([att.file_path])
    } catch {
      // Mantém soft delete mesmo se o storage falhar
    }
  },
}
